<template>
    <div ref="autoCompleteWrapper" class="layout-header-search relative" role="search">
        <form class="form-control py-4 px-6 md:p-0 border-t md:border-t-0" @submit.prevent="submitSearch()">
            <label class="input input-lg input-new flex items-center p-0 gap-2">
                <input
                    ref="searchInput"
                    v-model="term"
                    type="text"
                    :placeholder="t('layout.header.actions.search.placeholder')"
                    :name="t('layout.header.actions.search.placeholder')"
                    class="grow h-full py-1 px-4 text-placeholder placeholder:text-placeholder text-lg"
                    tabindex="0"
                    @keydown.esc="resetSearch()"
                    @keydown.up="focusPreviousElement($event)"
                    @keydown.down="focusNextElement($event)"
                >
                <div v-if="loading" class="btn btn-ghost">
                    <span class="loading w-8 h-8" />
                </div>
                <button
                    v-else
                    type="submit"
                    class="btn btn-ghost"
                    aria-label="Suche">
                    <MiscIcon name="search" size="xl" />
                </button>
            </label>
        </form>

        <template v-if="showResults">
            <div class="absolute top-full left-0 z-50 w-full search-results h-screen md:h-fit">
                <div class="flex flex-col bg-white border border-border rounded pt-4 gap-2 h-fit">
                    <div class="flex justify-end pr-4 ">
                        <button class="flex items-center font-semibold" @click="showResults = false">
                            <span class="mr-2">
                                {{ t('layout.header.actions.search.close') }}
                            </span>
                            <MiscIcon name="close" />
                        </button>
                    </div>
                    <div v-if="searchResult?.length > 0" class="text-lg">
                        <template v-if="config.showSearchSuggest">
                            <MiscLink
                                v-for="(result, index) in searchResult"
                                :key="index"
                                :to="result.url"
                                tabindex="0"
                                class="border-b p-1 h-12 flex items-center justify-between hover:underline focus:underline py-4 px-4"
                                :class="{'underline' : index === selectedElement}"
                            >
                                <div class="flex gap-2 items-center">
                                    <MiscImage v-if="result.image" :src="result.image" class="h-11 w-11 py-1"/>
                                    <div class="focus:underline">
                                        {{ result.name }}
                                    </div>
                                </div>
                                <div v-if="result.image === null">
                                    {{ t('layout.header.actions.search.suggestion') }}
                                </div>
                            </MiscLink>
                        </template>
                        <MiscLink
                            :ref="`searchResult_${searchResult?.length}`"
                            :to="`/search?search=${term}`"
                            tabindex="0"
                            class="font-bold flex justify-between items-center p-1 hover:underline focus:underline px-4 h-12 "
                            :class="{'underline' : searchResult?.length === selectedElement}"
                        >
                            <div>{{ t('layout.header.actions.search.showAll') }}</div>
                            <MiscIcon
                                name="chevron-right"
                                size="lg"
                            />
                        </MiscLink>
                    </div>
                    <template v-else-if="!resultsFound">
                        <div class="font-bold px-4 h-12 text-lg flex items-center">
                            {{ t('layout.header.actions.search.noResults') }}
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { watchDebounced } from '@vueuse/core'
import {type Ref, ref } from 'vue'
import {useEpoq, useLocalisation} from '#imports'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const route = useRoute();
const { typeAhead } = useEpoq()
const searchInput = ref()
const term: Ref<string> = ref(route?.query?.search as string)
const searchResult = ref(null)
const showResults = ref(false)
const autoCompleteWrapper = ref()
const loading = ref(false)
const config = useRuntimeConfig().public

onClickOutside(autoCompleteWrapper, () => resetSearch())

watchDebounced(
    term,
    () => {
        // if search submitted via enter don't search via typeahead
        if(!searchSubmitted.value) {
            search()
        }
        searchSubmitted.value = false
    },
    {
        debounce: 500
    }
)

const resultsFound = computed(() => {
    return searchResult?.value?.product || searchResult?.value?.suggestions?.length > 0 || searchResult?.value?.categories?.length > 0
})

watch(() => route.fullPath,
    (value) => {
        if(value !== `/search?search=${term.value}`) {
            term.value = ''
        }
        resetSearch()
    },
)

const searchSubmitted = ref(false)

const search = async function () {
    if (term.value !== '') {
        loading.value = true
        searchResult.value = await typeAhead(term.value)
        loading.value = false
        showResults.value = true
    }
}

function resetSearch () {
    showResults.value = false
    selectedElement.value = null
    searchResult.value = null
    searchInput.value.blur()
}

const submitSearch = async function () {
    if (term.value === '') {
        return
    }
    const { navigateToI18n } = useLocalisation()

    if(selectedElement.value && searchResult.value && selectedElement.value >= 0 && selectedElement.value < searchResult.value.length) {
        await navigateToI18n(searchResult.value[selectedElement.value].url)
        showResults.value = false
        return
    }

    searchSubmitted.value = true
    await navigateToI18n(`/search?search=${term.value}`)
}

const selectedElement: Ref<null|number> =  ref(null)
const focusPreviousElement = function(event: Event) {
    event.preventDefault()
    if(selectedElement.value === 0) {
        selectedElement.value = null
    } else {
        selectedElement.value = selectedElement.value && (selectedElement.value - 1) < 0 ?  null : selectedElement.value - 1
    }
}

const focusNextElement = function(event: Event) {
    event.preventDefault()
    if(selectedElement.value === null) {
        selectedElement.value = 0
    } else {
        selectedElement.value = Math.min(selectedElement.value + 1, searchResult?.value?.length)
    }
}
</script>
